import { z } from 'zod';
import {
  passwordSchema,
  usernameSchema,
  emailSchema,
  requiredString,
  optionalString,
  postcodeSchema,
  healthCardSchema,
  dollarAmountSchema,
} from 'services/FormValidation';

/**
 * Zod schemas and inferred TypeScript types for each form.
 * Forms are grouped by phase. Add new form schemas here as they are migrated.
 */

// --- Phase 1: Simple Forms ---

export const loginSchema = z.object({
  tenant: z
    .string()
    .transform((it) => it?.toLowerCase())
    .optional(),
  username: usernameSchema,
  password: passwordSchema,
});
export type LoginValues = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    oldPassword: passwordSchema,
    newPassword1: passwordSchema,
    newPassword2: passwordSchema,
  })
  .refine((data) => data.newPassword2 === data.newPassword1, {
    message: 'Passwords do not match',
    path: ['newPassword2'],
  });
export type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

export const setCredentialsSchema = z
  .object({
    username: usernameSchema,
    password: passwordSchema,
    password2: passwordSchema,
    admin_code: requiredString,
  })
  .refine((data) => data.password2 === data.password, {
    message: 'Passwords do not match',
    path: ['password2'],
  });
export type SetCredentialsValues = z.infer<typeof setCredentialsSchema>;

export const registrationCredentialsSchema = z
  .object({
    email: emailSchema,
    username: usernameSchema,
    password: passwordSchema,
    password2: passwordSchema,
    terms: z.literal(true, 'You must accept the terms'),
  })
  .refine((data) => data.password === data.password2, {
    message: 'Passwords do not match',
    path: ['password2'],
  });
export type RegistrationCredentialsValues = z.infer<typeof registrationCredentialsSchema>;

export const personalDetailsSchema = z.object({
  first_name: requiredString,
  last_name: requiredString,
  preferred_name: z.string(),
  preferred_pronoun: z.string(),
  date_of_birth: z.any().refine((val) => !!val, 'Must input the date of birth!'),
  gender: z.union([z.literal('Male'), z.literal('Female')], 'Must select a gender!'),
});
export type PersonalDetailsValues = z.infer<typeof personalDetailsSchema>;

export const promoCodeSchema = z.object({
  code: requiredString,
});
export type PromoCodeValues = z.infer<typeof promoCodeSchema>;

export const corporateCodeSchema = z.object({
  code: requiredString,
});
export type CorporateCodeValues = z.infer<typeof corporateCodeSchema>;

export const changePhoneSchema = z.object({
  phone: requiredString,
  code: optionalString,
});
export type ChangePhoneValues = z.infer<typeof changePhoneSchema>;

export const postcodeCheckSchema = z.object({
  postcode: postcodeSchema,
});
export type PostcodeCheckValues = z.infer<typeof postcodeCheckSchema>;

export const ohipFieldsSchema = z.object({
  health_number: healthCardSchema,
});
export type OHIPFieldsValues = z.infer<typeof ohipFieldsSchema>;

export const chargePatientSchema = z.object({
  amount: dollarAmountSchema,
  description: requiredString,
  memo: optionalString,
  tax_exempt: z.boolean().optional(),
});
export type ChargePatientValues = z.infer<typeof chargePatientSchema>;
